import React from 'react'
import { Tooltip } from '@surecompanies/core_components'
import { CurrencyFormatter } from 'helpers/formatters'
import dayjs from 'dayjs'

import { getShortText } from '../../PaymentAssignment/Utils/payment-assignment.helper'
import {
  getApplicationDetailsPath,
  getEmployeeCoveragePath,
} from 'modules/qle/utils/qleCommonLinks'
import {
  BenefitRow,
  BenefitSortKey,
} from '../../../api/reconciliation-v2-api-client'
import { colors, font, typography } from '../tokens'
import {
  buildBenefitLabel,
  buildPeriodKey,
  copyToClipboard,
  DISCREPANCY_EPSILON,
  EMPLOYEE_COL_WIDTH,
  HideableColKey,
  isTerminated,
  monthCoverage,
  primaryInsured,
} from './helpers'
import { OPEN_IN_APP_ENGINE_LABEL } from '../recon-v2-helpers'
import { BadgeBubble, CopyBtn, IconLink, TerminatedBadge } from './pills'
import MonthCell from './MonthCell'
import { CopySvg, ExternalLinkSvg, HistorySvg, TicketSvg } from './icons'
import styles from './BenefitsTable.module.scss'

/**
 * One benefit <tr>: sticky Insured cell, optional Employee cell, hideable info
 * columns, sticky Expected, the month cells (via MonthCell), the Sum/Diff
 * summary columns, and the Zendesk/activity actions. Imports the SHARED
 * BenefitsTable.module.scss so its hashed sticky/warn/hover classes match the
 * rest of the table (hard rule #1).
 */

const currencyFormatter = new CurrencyFormatter()

interface InfoColumn {
  key: Exclude<HideableColKey, 'employeeName'>
  label: string
  sortKey?: BenefitSortKey
  minWidth: number
  nowrap?: boolean
  render: (row: BenefitRow) => React.ReactNode
}

interface BenefitTableRowProps {
  row: BenefitRow
  year: number
  showDetails: boolean
  employeeVisible: boolean
  visibleInfoCols: InfoColumn[]
  visibleMonthIndices: number[]
  onAdjust: (row: BenefitRow, periodKey: string) => void
  onOpenActivity: (args: {
    benefitId: string
    benefitLabel: string
    month: string
  }) => void
  onOpenRowActivity: (row: BenefitRow) => void
  onOpenZendesk: (args: { benefitId: string; employeeName: string }) => void
}

const BenefitTableRow: React.FC<BenefitTableRowProps> = ({
  row,
  year,
  showDetails,
  employeeVisible,
  visibleInfoCols,
  visibleMonthIndices,
  onAdjust,
  onOpenActivity,
  onOpenRowActivity,
  onOpenZendesk,
}) => {
  const insured = primaryInsured(row)
  const benefitLabel = buildBenefitLabel(row)
  const coverageHref = getEmployeeCoveragePath(row.employeePublicKey)
  const expectedVal = row.carrierExpected ?? row.surecoExpected
  const hasTickets = row.ticketCount > 0
  // Row summary across ALL visible months: total paid, and paid − expected
  // (per-month expected, falling back to the benefit default). Diff is null
  // when no month carries an expected.
  //
  // Out-of-coverage months used to be skipped outright. Now that operators can
  // allocate to them, skipping would make the row total UNDER-REPORT money that
  // genuinely exists — silently, since nothing about a wrong total looks wrong.
  // So paid always counts. Expected does NOT fall back to the benefit default
  // out of window: nothing was owed for a month the benefit was not in force,
  // and inventing a premium there would inflate Sum Expected and make DIFF
  // meaningless. A stored per-month expected still counts if the server has one
  // (it snapshots the plan cost on first allocation), which keeps this sum
  // reconciling with the figures the cells display.
  const rowDefaultExpected = row.carrierExpected ?? row.surecoExpected
  let sumPaid = 0
  let sumExpected = 0
  let anyExpected = false
  for (const mi of visibleMonthIndices) {
    const { outOfRange } = monthCoverage(row, year, mi)
    const t = row.monthlyTotals[buildPeriodKey(year, mi)]
    sumPaid += t?.totalPaid ?? 0
    const exp = outOfRange ? t?.expected : (t?.expected ?? rowDefaultExpected)
    if (exp != null) {
      sumExpected += exp
      anyExpected = true
    }
  }
  const rowSum = Number(sumPaid.toFixed(2))
  const rowSumExpected = anyExpected ? Number(sumExpected.toFixed(2)) : null
  const rowDiff = anyExpected
    ? Number((sumPaid - sumExpected).toFixed(2))
    : null
  const diffWarn = rowDiff != null && Math.abs(rowDiff) > DISCREPANCY_EPSILON

  return (
    <tr>
      <td className={styles['sticky-insured']}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <span>
            {insured ? (
              `${insured.firstName} ${insured.lastName}`
            ) : (
              <span
                style={{
                  color: colors.text.faint,
                  fontStyle: 'italic',
                }}
              >
                {/* No insured-person data: fall back to the employee name, or
                    a dash when that's empty too, so the cell is never blank. */}
                {row.employeeName || '-'}
              </span>
            )}
          </span>
          {/* The benefit id is TEXT, not a link. It used to be an anchor to the
              person's coverage page — a benefit id that navigated somewhere
              about the employee, while the Employee cell beside it already
              linked to exactly that page. The coverage link now lives there,
              next to the name it belongs to, and the id's own destination —
              its application page — is the jump-out beside it. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              title={row.benefitId}
              style={{
                fontFamily: font.mono,
                fontSize: 12,
                color: colors.grey[400],
              }}
            >
              {getShortText(row.benefitId, 4)}
            </span>
            <CopyBtn
              aria-label="Copy benefit ID"
              onClick={() => copyToClipboard(row.benefitId)}
            >
              <CopySvg size={13} />
            </CopyBtn>
            {/* Unconditional: `benefitId` is non-nullable, and nothing on the
                wire says whether an application exists — a "does it?" guard
                would be a guess that hides the control on good rows.

                The id alone is the whole address. This lands on the one
                application, the way Payment assignment's "App Engine Link"
                does, so there is no filtered list to miss and no coverage year
                or effective date to reconcile against it. */}
            <IconLink
              href={getApplicationDetailsPath(row.benefitId)}
              label={OPEN_IN_APP_ENGINE_LABEL}
            >
              <ExternalLinkSvg size={12} />
            </IconLink>
          </div>
        </div>
      </td>

      {employeeVisible && (
        <td
          style={{
            width: EMPLOYEE_COL_WIDTH,
            minWidth: EMPLOYEE_COL_WIDTH,
            maxWidth: EMPLOYEE_COL_WIDTH,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
            }}
          >
            <a
              href={`/employees/${row.employeePublicKey}/coverage`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 14,
                lineHeight: '20px',
                color: colors.text.primary,
                fontWeight: typography.weight.regular,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.employeeName}
            </a>
            {/* The coverage link the benefit id used to carry, moved to the
                column it is actually about. The name above stays clickable —
                this is the same destination stated explicitly, for a cell whose
                name is often truncated to the point of not looking like one. */}
            {coverageHref ? (
              <IconLink href={coverageHref} label="Open employee coverage">
                <ExternalLinkSvg size={12} />
              </IconLink>
            ) : null}
            {(isTerminated(row.employmentStatus) || row.terminationDate) && (
              <Tooltip
                title={
                  row.terminationDate
                    ? `Terminated on ${dayjs(row.terminationDate).format('MMM D, YYYY')}`
                    : 'Employee is terminated'
                }
              >
                <TerminatedBadge>T</TerminatedBadge>
              </Tooltip>
            )}
          </div>
        </td>
      )}

      {visibleInfoCols.map((c) => (
        <td key={c.key} style={c.nowrap ? { whiteSpace: 'nowrap' } : undefined}>
          {c.render(row)}
        </td>
      ))}

      <td className={styles['sticky-expected']}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            whiteSpace: 'nowrap',
            ...typography.numNormal,
          }}
        >
          {expectedVal == null ? (
            <span style={{ color: colors.text.disabled }}>—</span>
          ) : (
            currencyFormatter.safeFormat(String(expectedVal), '-')
          )}
        </div>
      </td>

      {visibleMonthIndices.map((mi) => {
        const { outOfRange, title } = monthCoverage(row, year, mi)
        return (
          <MonthCell
            key={mi}
            row={row}
            mi={mi}
            year={year}
            benefitLabel={benefitLabel}
            showDetails={showDetails}
            outOfRange={outOfRange}
            coverageTitle={title}
            onAdjust={onAdjust}
            onOpenActivity={onOpenActivity}
          />
        )
      })}

      <td
        className={styles.num}
        style={{
          textAlign: 'right',
          whiteSpace: 'nowrap',
          ...typography.numNormal,
          color:
            rowSumExpected == null ? colors.text.disabled : colors.text.primary,
        }}
      >
        {rowSumExpected == null
          ? '—'
          : currencyFormatter.safeFormat(String(rowSumExpected), '$0.00')}
      </td>
      <td
        className={styles.num}
        style={{
          textAlign: 'right',
          whiteSpace: 'nowrap',
          ...typography.numNormal,
        }}
      >
        {currencyFormatter.safeFormat(String(rowSum), '$0.00')}
      </td>
      <td
        className={styles.num}
        style={{
          textAlign: 'right',
          whiteSpace: 'nowrap',
          ...typography.numNormal,
          color:
            rowDiff == null
              ? colors.text.disabled
              : diffWarn
                ? colors.status.underpaid.fg
                : colors.text.primary,
        }}
      >
        {rowDiff == null
          ? '—'
          : currencyFormatter.safeFormat(String(rowDiff), '$0.00')}
      </td>

      <td className={styles.zendesk}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <button
            type="button"
            className={styles.rowIconBtn}
            title="Activity log"
            onClick={() => onOpenRowActivity(row)}
          >
            <HistorySvg size={15} />
          </button>
          <button
            type="button"
            className={`${styles.rowIconBtn}${
              hasTickets ? ` ${styles.active}` : ''
            }`}
            title={
              hasTickets
                ? `${row.ticketCount} support ticket${row.ticketCount > 1 ? 's' : ''} linked`
                : 'Link support ticket'
            }
            onClick={() =>
              onOpenZendesk({
                benefitId: row.benefitId,
                employeeName: row.employeeName,
              })
            }
          >
            <TicketSvg size={16} />
            {hasTickets && <BadgeBubble>{row.ticketCount}</BadgeBubble>}
          </button>
        </div>
      </td>
    </tr>
  )
}

export default BenefitTableRow
